import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import {
    Autocomplete,
    Box,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useDeferredValue, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatGroupTitle, getToolHref } from "../utils/tools";

const getToolSearchText = (tool) =>
    [
        tool.title,
        tool.description,
        tool.path?.replaceAll("-", " "),
        tool.id?.replaceAll("-", " "),
        tool.group,
        tool.group?.replaceAll("-", " "),
        tool.group ? formatGroupTitle(tool.group) : "General Tools",
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

const filterTools = (tools, query) => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return tools;

    return tools.filter((tool) =>
        getToolSearchText(tool).includes(normalizedQuery)
    );
};

export default function ListViewer({ toolsList }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const deferredSearchQuery = useDeferredValue(searchQuery);
    const filteredTools = filterTools(toolsList, deferredSearchQuery);
    const hasActiveSearch = deferredSearchQuery.trim().length > 0;
    const groupedTools = filteredTools.reduce((acc, tool) => {
        const group = tool.group || "Other";
        if (!acc[group]) acc[group] = [];
        acc[group].push(tool);
        return acc;
    }, {});
    const resultsCount = filteredTools.length;
    const totalCount = toolsList.length;

    return (
        <Stack spacing={4} sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "28px",
                    border: "1px solid",
                    borderColor: "var(--app-border)",
                    boxShadow: "var(--app-shadow)",
                    background: `linear-gradient(145deg, ${alpha(
                        theme.palette.background.paper,
                        theme.palette.mode === "dark" ? 0.88 : 0.92
                    )}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    p: { xs: 2.25, sm: 3 },
                }}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at top right, ${alpha(
                            theme.palette.secondary.main,
                            0.18
                        )}, transparent 34%), radial-gradient(circle at left center, ${alpha(
                            theme.palette.primary.main,
                            0.14
                        )}, transparent 36%)`,
                        pointerEvents: "none",
                    }}
                />
                <Stack
                    spacing={2}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                    }}>
                    <Stack
                        direction={{ xs: "column", lg: "row" }}
                        spacing={{ xs: 2, lg: 3 }}
                        alignItems={{ xs: "stretch", lg: "center" }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    display: "block",
                                    color: "secondary.main",
                                    fontWeight: 700,
                                    letterSpacing: "0.14em",
                                    mb: 0.5,
                                }}>
                                Tool Search
                            </Typography>
                            <Typography
                                variant="h4"
                                color="text.primary"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: "-0.03em",
                                    mb: 1,
                                }}>
                                Find the right tool faster
                            </Typography>
                            <Typography color="text.secondary">
                                Search by tool name, category, or keyword and
                                jump straight to the utility you need.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: { lg: 480 },
                            }}>
                            <Autocomplete
                                freeSolo
                                fullWidth
                                openOnFocus
                                autoHighlight
                                clearOnBlur={false}
                                options={toolsList}
                                inputValue={searchQuery}
                                getOptionLabel={(option) =>
                                    typeof option === "string"
                                        ? option
                                        : option.title
                                }
                                filterOptions={(options, state) =>
                                    filterTools(options, state.inputValue).slice(
                                        0,
                                        8
                                    )
                                }
                                onInputChange={(_, value) => {
                                    setSearchQuery(value);
                                }}
                                onChange={(_, selectedTool) => {
                                    if (
                                        !selectedTool ||
                                        typeof selectedTool === "string"
                                    ) {
                                        return;
                                    }

                                    navigate(getToolHref(selectedTool));
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                noOptionsText="No matching tools"
                                slotProps={{
                                    paper: {
                                        elevation: 0,
                                        sx: {
                                            mt: 1,
                                            borderRadius: "20px",
                                            border: "1px solid",
                                            borderColor: "var(--app-border)",
                                            background: "var(--app-surface)",
                                            boxShadow: "var(--app-shadow-strong)",
                                            overflow: "hidden",
                                        },
                                    },
                                }}
                                renderOption={(props, tool) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        key={tool.id}
                                        sx={{
                                            ...props.sx,
                                            p: 0,
                                            alignItems: "stretch",
                                            minHeight: "auto",
                                            "& + &": {
                                                mt: 0.75,
                                            },
                                        }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                width: "100%",
                                                px: 1.5,
                                                py: 1.25,
                                                borderRadius: "16px",
                                                transition:
                                                    "background 180ms ease, transform 180ms ease",
                                                "&:hover": {
                                                    background: alpha(
                                                        theme.palette.secondary
                                                            .main,
                                                        0.08
                                                    ),
                                                    transform:
                                                        "translateY(-1px)",
                                                },
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    borderRadius: "12px",
                                                    background: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.12
                                                    ),
                                                    color: "primary.main",
                                                    flexShrink: 0,
                                                }}>
                                                {tool.icon}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body1"
                                                    color="text.primary"
                                                    sx={{
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                    }}>
                                                    {tool.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.25,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}>
                                                    {tool.group
                                                        ? formatGroupTitle(
                                                              tool.group
                                                          )
                                                        : "General Tools"}
                                                </Typography>
                                            </Box>
                                            <ArrowOutwardRoundedIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: "text.secondary",
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search tools"
                                        placeholder="Try calculator, QR, password..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchRoundedIcon
                                                            sx={{
                                                                color: "secondary.main",
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                    {
                                                        params.InputProps
                                                            .startAdornment
                                                    }
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        minHeight: { xs: 56, sm: 62 },
                                        borderRadius: "18px",
                                        background: alpha(
                                            theme.palette.background.paper,
                                            theme.palette.mode === "dark"
                                                ? 0.78
                                                : 0.92
                                        ),
                                        boxShadow: `0 12px 24px ${alpha(
                                            theme.palette.common.black,
                                            theme.palette.mode === "dark"
                                                ? 0.22
                                                : 0.08
                                        )}`,
                                        "& fieldset": {
                                            borderColor: "secondary.main",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "secondary.main",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderWidth: 1,
                                            borderColor: "secondary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "secondary.main",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "secondary.main",
                                    },
                                }}
                            />
                        </Box>
                    </Stack>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            width: "fit-content",
                            px: 1.25,
                            py: 0.75,
                            borderRadius: "999px",
                            border: "1px solid",
                            borderColor: "var(--app-border)",
                            background: alpha(
                                theme.palette.background.paper,
                                theme.palette.mode === "dark" ? 0.55 : 0.74
                            ),
                        }}>
                        {hasActiveSearch
                            ? `Showing ${resultsCount} of ${totalCount} tools for "${deferredSearchQuery.trim()}"`
                            : `Browse ${totalCount} tools in this list`}
                    </Typography>
                </Stack>
            </Box>

            {resultsCount === 0 ? (
                <Box
                    sx={{
                        borderRadius: "24px",
                        border: "1px dashed",
                        borderColor: "var(--app-border)",
                        background: "var(--app-card)",
                        boxShadow: "var(--app-shadow)",
                        px: { xs: 2.5, sm: 4 },
                        py: { xs: 4, sm: 5 },
                        textAlign: "center",
                    }}>
                    <Typography
                        variant="h5"
                        color="text.primary"
                        sx={{ fontWeight: 700, mb: 1 }}>
                        No tools match that search
                    </Typography>
                    <Typography color="text.secondary">
                        Try a broader keyword, another category name, or pick a
                        suggestion from the autocomplete list.
                    </Typography>
                </Box>
            ) : (
                Object.entries(groupedTools).map(([group, tools]) => (
                    <div key={group}>
                        <Typography
                            variant="h4"
                            color="text.primary"
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                letterSpacing: "-0.02em",
                            }}>
                            {group === "Other"
                                ? "General Tools"
                                : formatGroupTitle(group)}
                        </Typography>

                        <Grid container spacing={3}>
                            {tools.map((tool, index) => {
                                const linkPath = getToolHref(tool);

                                return (
                                    <Grid
                                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                        key={tool.id}>
                                        <Box
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                textAlign: "center",
                                                transition:
                                                    "transform 200ms ease, box-shadow 200ms ease",
                                                boxShadow: "var(--app-shadow)",
                                                background: "var(--app-card)",
                                                border: "1px solid",
                                                borderColor: "var(--app-border)",
                                                borderRadius: "16px",
                                                overflow: "hidden",
                                                position: "relative",
                                                "&:hover": {
                                                    transform:
                                                        "translateY(-3px)",
                                                    boxShadow:
                                                        "var(--app-shadow-strong)",
                                                },
                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    inset: 0,
                                                    background:
                                                        "radial-gradient(80% 80% at 0% 0%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0))",
                                                    opacity: 0,
                                                    transition:
                                                        "opacity 200ms ease",
                                                    pointerEvents: "none",
                                                },
                                                "&:hover::before": {
                                                    opacity: 1,
                                                },
                                                animation:
                                                    "fadeUp 520ms ease both",
                                                animationDelay: `${index * 40}ms`,
                                            }}>
                                            <Stack
                                                component={Link}
                                                to={linkPath}
                                                spacing={1}
                                                sx={{
                                                    padding: "14px",
                                                    textDecoration: "none",
                                                    position: "relative",
                                                    zIndex: 1,
                                                }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        textAlign: "left",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}>
                                                    <Typography
                                                        variant="body1"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 600 }}>
                                                        {tool.icon}
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 600 }}>
                                                        {tool.title}
                                                    </Typography>
                                                </Box>
                                                {tool.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary">
                                                        {tool.description}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </div>
                ))
            )}
        </Stack>
    );
}
